/**
 * Australian Area Codes and Coverage
 * Complete mapping of Australian telephone area codes to cities and states
 * for Telnyx phone number provisioning
 */

export interface AustralianAreaCode {
  code: string;
  state: string;
  stateCode: string; // NSW, VIC, QLD, etc.
  city: string;
  locality: string; // For Telnyx API
  administrativeArea: string; // For Telnyx API (state code)
  description: string;
}

/**
 * Comprehensive list of Australian area codes
 * Organized by area code prefix (02, 03, 07, 08)
 */
export const AUSTRALIAN_AREA_CODES: AustralianAreaCode[] = [
  // ============================================================================
  // AREA CODE 02 - New South Wales & Australian Capital Territory
  // ============================================================================
  {
    code: '02',
    state: 'New South Wales',
    stateCode: 'NSW',
    city: 'Sydney',
    locality: 'Sydney',
    administrativeArea: 'NSW',
    description: 'Sydney (NSW) - Capital city'
  },
  {
    code: '02',
    state: 'New South Wales',
    stateCode: 'NSW',
    city: 'Newcastle',
    locality: 'Newcastle',
    administrativeArea: 'NSW',
    description: 'Newcastle (NSW) - Coastal city'
  },
  {
    code: '02',
    state: 'New South Wales',
    stateCode: 'NSW',
    city: 'Wollongong',
    locality: 'Wollongong',
    administrativeArea: 'NSW',
    description: 'Wollongong (NSW) - South coast'
  },
  {
    code: '02',
    state: 'New South Wales',
    stateCode: 'NSW',
    city: 'Central Coast',
    locality: 'Central Coast',
    administrativeArea: 'NSW',
    description: 'Central Coast (NSW) - Gosford area'
  },
  {
    code: '02',
    state: 'Australian Capital Territory',
    stateCode: 'ACT',
    city: 'Canberra',
    locality: 'Canberra',
    administrativeArea: 'ACT',
    description: 'Canberra (ACT) - National capital'
  },
  {
    code: '02',
    state: 'New South Wales',
    stateCode: 'NSW',
    city: 'Wagga Wagga',
    locality: 'Wagga Wagga',
    administrativeArea: 'NSW',
    description: 'Wagga Wagga (NSW) - Riverina region'
  },
  {
    code: '02',
    state: 'New South Wales',
    stateCode: 'NSW',
    city: 'Albury',
    locality: 'Albury',
    administrativeArea: 'NSW',
    description: 'Albury (NSW) - Border city'
  },

  // ============================================================================
  // AREA CODE 03 - Victoria & Tasmania
  // ============================================================================
  {
    code: '03',
    state: 'Victoria',
    stateCode: 'VIC',
    city: 'Melbourne',
    locality: 'Melbourne',
    administrativeArea: 'VIC',
    description: 'Melbourne (VIC) - Capital city'
  },
  {
    code: '03',
    state: 'Victoria',
    stateCode: 'VIC',
    city: 'Geelong',
    locality: 'Geelong',
    administrativeArea: 'VIC',
    description: 'Geelong (VIC) - Coastal city'
  },
  {
    code: '03',
    state: 'Victoria',
    stateCode: 'VIC',
    city: 'Ballarat',
    locality: 'Ballarat',
    administrativeArea: 'VIC',
    description: 'Ballarat (VIC) - Regional city'
  },
  {
    code: '03',
    state: 'Victoria',
    stateCode: 'VIC',
    city: 'Bendigo',
    locality: 'Bendigo',
    administrativeArea: 'VIC',
    description: 'Bendigo (VIC) - Regional city'
  },
  {
    code: '03',
    state: 'Tasmania',
    stateCode: 'TAS',
    city: 'Hobart',
    locality: 'Hobart',
    administrativeArea: 'TAS',
    description: 'Hobart (TAS) - Capital city'
  },
  {
    code: '03',
    state: 'Tasmania',
    stateCode: 'TAS',
    city: 'Launceston',
    locality: 'Launceston',
    administrativeArea: 'TAS',
    description: 'Launceston (TAS) - Northern city'
  },
  {
    code: '03',
    state: 'Tasmania',
    stateCode: 'TAS',
    city: 'Devonport',
    locality: 'Devonport',
    administrativeArea: 'TAS',
    description: 'Devonport (TAS) - Northwest coast'
  },

  // ============================================================================
  // AREA CODE 07 - Queensland
  // ============================================================================
  {
    code: '07',
    state: 'Queensland',
    stateCode: 'QLD',
    city: 'Brisbane',
    locality: 'Brisbane',
    administrativeArea: 'QLD',
    description: 'Brisbane (QLD) - Capital city'
  },
  {
    code: '07',
    state: 'Queensland',
    stateCode: 'QLD',
    city: 'Gold Coast',
    locality: 'Gold Coast',
    administrativeArea: 'QLD',
    description: 'Gold Coast (QLD) - Tourist city'
  },
  {
    code: '07',
    state: 'Queensland',
    stateCode: 'QLD',
    city: 'Sunshine Coast',
    locality: 'Sunshine Coast',
    administrativeArea: 'QLD',
    description: 'Sunshine Coast (QLD) - Coastal region'
  },
  {
    code: '07',
    state: 'Queensland',
    stateCode: 'QLD',
    city: 'Townsville',
    locality: 'Townsville',
    administrativeArea: 'QLD',
    description: 'Townsville (QLD) - North Queensland'
  },
  {
    code: '07',
    state: 'Queensland',
    stateCode: 'QLD',
    city: 'Cairns',
    locality: 'Cairns',
    administrativeArea: 'QLD',
    description: 'Cairns (QLD) - Far North Queensland'
  },
  {
    code: '07',
    state: 'Queensland',
    stateCode: 'QLD',
    city: 'Toowoomba',
    locality: 'Toowoomba',
    administrativeArea: 'QLD',
    description: 'Toowoomba (QLD) - Darling Downs'
  },
  {
    code: '07',
    state: 'Queensland',
    stateCode: 'QLD',
    city: 'Mackay',
    locality: 'Mackay',
    administrativeArea: 'QLD',
    description: 'Mackay (QLD) - Central Queensland'
  },
  {
    code: '07',
    state: 'Queensland',
    stateCode: 'QLD',
    city: 'Rockhampton',
    locality: 'Rockhampton',
    administrativeArea: 'QLD',
    description: 'Rockhampton (QLD) - Central Queensland'
  },

  // ============================================================================
  // AREA CODE 08 - Western Australia, South Australia & Northern Territory
  // ============================================================================
  {
    code: '08',
    state: 'Western Australia',
    stateCode: 'WA',
    city: 'Perth',
    locality: 'Perth',
    administrativeArea: 'WA',
    description: 'Perth (WA) - Capital city'
  },
  {
    code: '08',
    state: 'Western Australia',
    stateCode: 'WA',
    city: 'Fremantle',
    locality: 'Fremantle',
    administrativeArea: 'WA',
    description: 'Fremantle (WA) - Port city'
  },
  {
    code: '08',
    state: 'Western Australia',
    stateCode: 'WA',
    city: 'Mandurah',
    locality: 'Mandurah',
    administrativeArea: 'WA',
    description: 'Mandurah (WA) - Coastal city'
  },
  {
    code: '08',
    state: 'South Australia',
    stateCode: 'SA',
    city: 'Adelaide',
    locality: 'Adelaide',
    administrativeArea: 'SA',
    description: 'Adelaide (SA) - Capital city'
  },
  {
    code: '08',
    state: 'South Australia',
    stateCode: 'SA',
    city: 'Mount Gambier',
    locality: 'Mount Gambier',
    administrativeArea: 'SA',
    description: 'Mount Gambier (SA) - Southeast'
  },
  {
    code: '08',
    state: 'Northern Territory',
    stateCode: 'NT',
    city: 'Darwin',
    locality: 'Darwin',
    administrativeArea: 'NT',
    description: 'Darwin (NT) - Capital city'
  },
  {
    code: '08',
    state: 'Northern Territory',
    stateCode: 'NT',
    city: 'Alice Springs',
    locality: 'Alice Springs',
    administrativeArea: 'NT',
    description: 'Alice Springs (NT) - Central Australia'
  },
  {
    code: '08',
    state: 'Northern Territory',
    stateCode: 'NT',
    city: 'Palmerston',
    locality: 'Palmerston',
    administrativeArea: 'NT',
    description: 'Palmerston (NT) - Greater Darwin'
  },
  {
    code: '08',
    state: 'Northern Territory',
    stateCode: 'NT',
    city: 'Katherine',
    locality: 'Katherine',
    administrativeArea: 'NT',
    description: 'Katherine (NT) - Northern region'
  }
];

/**
 * Get area codes filtered by state
 */
export const getAreaCodesByState = (stateCode: string): AustralianAreaCode[] => {
  return AUSTRALIAN_AREA_CODES.filter(ac => ac.stateCode === stateCode);
};

/**
 * Get all unique states
 */
export const getAustralianStates = (): Array<{ code: string; name: string }> => {
  const statesMap = new Map<string, string>();
  AUSTRALIAN_AREA_CODES.forEach(ac => {
    if (!statesMap.has(ac.stateCode)) {
      statesMap.set(ac.stateCode, ac.state);
    }
  });
  return Array.from(statesMap.entries()).map(([code, name]) => ({ code, name }));
};

/**
 * Get area code by city name
 */
export const getAreaCodeByCity = (city: string): AustralianAreaCode | undefined => {
  return AUSTRALIAN_AREA_CODES.find(ac =>
    ac.city.toLowerCase() === city.toLowerCase()
  );
};

/**
 * Format area code for display in dropdown
 */
export const formatAreaCodeDisplay = (areaCode: AustralianAreaCode): string => {
  return `${areaCode.code} - ${areaCode.city} (${areaCode.stateCode})`;
};
